import React from "react";
import { ReactComponent as DefaultImage } from "../../../assets/building.svg";
import { parseDeadline } from "../DeadlineFilter/utils";
import "./styles.css";

type Props = {
    housing: any;
    onClick?: () => void;
    /** Allows the card to be embedded in lists other than the housing catalog. */
    as?: "li" | "div";
    /** Optional action slot used by search and recommendation surfaces. */
    action?: React.ReactNode;
    hideAction?: boolean;
    meta?: React.ReactNode;
    price?: {
        label?: React.ReactNode;
        value?: React.ReactNode;
        note?: React.ReactNode;
    };
    className?: string;
    labels?: {
        showBuilder?: boolean;
        defaultBuilderLabel?: string;
        unknownAddressLabel?: string;
        unknownDeadlineLabel?: string;
        priceLabel?: string;
        priceFallbackLabel?: string;
        priceNoteLabel?: string;
        priceFallbackNoteLabel?: string;
        openLabel?: string;
    };
};

const formatPrice = (value: number | null | undefined) => {
    if (!value) return null;
    return new Intl.NumberFormat("ru-RU").format(value);
};

export const HousingItem: React.FC<Props> = ({
    housing,
    onClick,
    as: Component = "li",
    action,
    hideAction = false,
    meta,
    price,
    className = "",
    labels,
}) => {
    const img = housing?.images?.[0] || null;
    const builder = housing?.builder?.trim();
    const address = housing?.address?.trim();
    const parsedDeadline = parseDeadline(housing?.deadline ?? "");
    const minPrice = formatPrice(housing?.minPrice);
    const maxPrice = formatPrice(housing?.maxPrice);
    const hasPriceRange = Boolean(minPrice && maxPrice);
    const photosCount = Array.isArray(housing?.images) ? housing.images.length : 0;
    const showBuilder = labels?.showBuilder ?? true;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <Component
            className={`housing-item_container ${className}`.trim()}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className="housing-item_image-container">
                {img ? (
                    <div className="housing-item_image">
                        <img
                            src={img}
                            className="housing-item_foreground-image"
                            alt={housing.housingName}
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                ) : (
                    <div className="housing-item_default-image">
                        <DefaultImage width="100%" height="100%" />
                    </div>
                )}
            </div>

            <div className="housing-item_info">
                <div className="housing-item_main">
                    {showBuilder && (
                        <div className="housing-item_kicker">
                            {builder || labels?.defaultBuilderLabel || "Объект недвижимости"}
                        </div>
                    )}

                    <div className="housing-item_title">{housing.housingName}</div>

                    <div className="housing-item_address">
                        {address || labels?.unknownAddressLabel || "Адрес уточняется"}
                    </div>

                    <div className="housing-item_meta">
                        {meta ?? (
                            <>
                                <span className="housing-item_meta-item">
                                    {parsedDeadline.unknown
                                        ? labels?.unknownDeadlineLabel || "Срок не указан"
                                        : `Сдача: ${parsedDeadline.label}`}
                                </span>
                                {photosCount ? (
                                    <span className="housing-item_meta-item">{photosCount} фото</span>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>

                <div className="housing-item_side">
                    <div className="housing-item_price-block">
                        <span className="housing-item_price-label">
                            {price?.label ?? (labels?.priceLabel || "Цена")}
                        </span>
                        <span className="housing-item_price-value">
                            {price?.value ?? (hasPriceRange
                                ? `${minPrice} - ${maxPrice}`
                                : labels?.priceFallbackLabel || "По запросу")}
                        </span>
                        <span className="housing-item_price-note">
                            {price?.note ?? (hasPriceRange
                                ? labels?.priceNoteLabel || "₽ за м²"
                                : labels?.priceFallbackNoteLabel || "Уточните у менеджера")}
                        </span>
                    </div>

                    {!hideAction && (action || (
                        <span className="housing-item_link">
                            {labels?.openLabel || "Открыть"}
                        </span>
                    ))}
                </div>
            </div>
        </Component>
    );
};
